import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, Card, CardContent, 
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, 
  DialogActions, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  CheckCircle, Link, LinkOff, AutoFixHigh, Refresh, PlayArrow, Visibility
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { expedientesApi, clientesApi, tecnicosApi } from '../../services/api';

interface ConfigAction {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  risk: 'low' | 'medium' | 'high';
  action: () => void;
  disabled?: boolean;
}

interface AsignacionResult {
  expediente_id: string;
  expediente_codigo: string;
  cliente_original?: string;
  cliente_encontrado: string | null;
  cliente_id: string | null;
  actualizado: boolean;
  error?: string;
}

interface SimilitudResult {
  porcentaje: number;
  razon: string;
}

interface CoincidenciaResult {
  tipo: 'exacta' | 'parcial' | 'sin_relacion';
  cliente_encontrado: any | null;
  confianza: number;
  razon: string;
}

const ConfiguracionGeneral: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estados para asignación de clientes
  const [asignacionLoading, setAsignacionLoading] = useState(false);
  const [asignacionResults, setAsignacionResults] = useState<AsignacionResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);
  const [clientes, setClientes] = useState<any[]>([]);
  const [expedientesSinAsignar, setExpedientesSinAsignar] = useState<any[]>([]);
  const [showAsignacionManual, setShowAsignacionManual] = useState(false);
  const [asignacionesTemporales, setAsignacionesTemporales] = useState<{[key: string]: string}>({});

  // Estados para análisis previo
  const [showAnalisisPrevio, setShowAnalisisPrevio] = useState(false);
  const [relacionesClaras, setRelacionesClaras] = useState<any[]>([]);
  const [relacionesDudosas, setRelacionesDudosas] = useState<any[]>([]);
  const [expedientesSinRelacion, setExpedientesSinRelacion] = useState<any[]>([]);

  // Estados para verificación de integridad
  const [verificacionLoading, setVerificacionLoading] = useState(false);
  const [verificacionResults, setVerificacionResults] = useState<any[]>([]);
  const [showVerificacion, setShowVerificacion] = useState(false);

  // ============================================
  // FUNCIONES MEJORADAS DEL SCRIPT NODE.JS
  // ============================================

  // Diccionario de abreviaciones inteligente
  const abreviaciones = {
    "J1": "JUZGADO 1", "J2": "JUZGADO 2", "J3": "JUZGADO 3", "J4": "JUZGADO 4", "J5": "JUZGADO 5",
    "MER": "MERIDA", "ALM": "ALMENDRALEJO", "DBEN": "DON BENITO", "VSER": "VILLANUEVA DE LA SERENA",
    "CC": "CASTUERA", "VBAR": "VILLAFRANCA DE LOS BARROS",
    "Aud": "AUDIENCIA", "Provincial": "PROVINCIAL", "INS": "INSTRUCCION",
    "PENAL": "PENAL", "CIVIL": "CIVIL", "MERCANTIL": "MERCANTIL", "CONTENCIOSO": "CONTENCIOSO",
    "FAMILIA": "FAMILIA", "SOCIAL": "SOCIAL", "VIOLENCIA": "VIOLENCIA", "GENERO": "GENERO"
  };

  // Función para normalizar nombres
  const normalizarNombre = (nombre: string): string => {
    let nombreNorm = nombre.toUpperCase().trim();
    
    // Aplicar abreviaciones
    Object.entries(abreviaciones).forEach(([abrev, completo]) => {
      const regex = new RegExp(`\\b${abrev}\\b`, "g");
      nombreNorm = nombreNorm.replace(regex, completo);
    });
    
    // Limpiar caracteres especiales y espacios múltiples
    nombreNorm = nombreNorm
      .replace(/[^A-Z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    
    return nombreNorm;
  };

  // Algoritmo de Levenshtein para calcular similitud
  const calcularLevenshtein = (str1: string, str2: string): number => {
    const len1 = str1.length;
    const len2 = str2.length;
    const matrix: number[][] = [];
    
    for (let i = 0; i <= len2; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= len1; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= len2; i++) {
      for (let j = 1; j <= len1; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    const maxLen = Math.max(len1, len2);
    return maxLen === 0 ? 100 : ((maxLen - matrix[len2][len1]) / maxLen) * 100;
  };

  // Función inteligente para calcular similitud
  const calcularSimilitudInteligente = (nombreExp: string, nombreCli: string): SimilitudResult => {
    const expNorm = normalizarNombre(nombreExp);
    const cliNorm = normalizarNombre(nombreCli);
    
    // Coincidencia exacta tras normalización
    if (expNorm === cliNorm) {
      return { porcentaje: 100, razon: "Coincidencia exacta tras normalización" };
    }
    
    // Filtrar palabras cortas
    const palabrasExp = expNorm.split(" ").filter(p => p.length > 2);
    const palabrasCli = cliNorm.split(" ").filter(p => p.length > 2);
    
    // Palabras importantes del sistema judicial
    const palabrasImportantes = [
      "PENAL", "CIVIL", "JUZGADO", "AUDIENCIA", "INSTRUCCION", 
      "MERIDA", "ALMENDRALEJO", "DON BENITO", "VILLANUEVA", "CASTUERA",
      "MERCANTIL", "CONTENCIOSO", "FAMILIA", "SOCIAL", "VIOLENCIA", "GENERO",
      "PROVINCIAL", "TRIBUNAL", "SUPREMO", "CONSTITUCIONAL"
    ];
    
    const importantesExp = palabrasExp.filter(p => palabrasImportantes.includes(p));
    const importantesCli = palabrasCli.filter(p => palabrasImportantes.includes(p));
    
    const coincidenciasImportantes = importantesExp.filter(p => importantesCli.includes(p));
    
    // Si hay coincidencias en palabras clave
    if (coincidenciasImportantes.length > 0) {
      const porcentaje = (coincidenciasImportantes.length / Math.max(importantesExp.length, importantesCli.length)) * 100;
      return { 
        porcentaje: Math.min(porcentaje, 95), 
        razon: `Coinciden palabras clave: ${coincidenciasImportantes.join(", ")}` 
      };
    }
    
    // Usar algoritmo Levenshtein para similitud textual
    const similitudTexto = calcularLevenshtein(expNorm, cliNorm);
    return { 
      porcentaje: similitudTexto, 
      razon: `Similitud de texto: ${similitudTexto.toFixed(1)}%` 
    };
  };

  // Función mejorada para analizar coincidencias
  const analizarCoincidenciaCliente = (clienteNombre: string, clientes: any[]): CoincidenciaResult => {
    if (!clienteNombre || clienteNombre.trim() === '') {
      return { tipo: 'sin_relacion', cliente_encontrado: null, confianza: 0, razon: 'Nombre de cliente vacío' };
    }

    const nombreBuscado = normalizarNombre(clienteNombre);
    
    // Buscar coincidencia exacta
    let clienteEncontrado = clientes.find(cliente => 
      normalizarNombre(cliente.nombre) === nombreBuscado
    );
    
    if (clienteEncontrado) {
      return { 
        tipo: 'exacta', 
        cliente_encontrado: clienteEncontrado, 
        confianza: 100,
        razon: 'Coincidencia exacta tras normalización'
      };
    }
    
    // Buscar la mejor coincidencia usando el algoritmo inteligente
    let mejorCoincidencia: { cliente: any; similitud: SimilitudResult } | null = null;
    let mejorPorcentaje = 0;
    
    for (const cliente of clientes) {
      const similitud = calcularSimilitudInteligente(clienteNombre, cliente.nombre);
      
      if (similitud.porcentaje > mejorPorcentaje) {
        mejorPorcentaje = similitud.porcentaje;
        mejorCoincidencia = { cliente, similitud };
      }
    }
    
    if (mejorCoincidencia && mejorPorcentaje >= 80) {
      return { 
        tipo: 'exacta', 
        cliente_encontrado: mejorCoincidencia.cliente, 
        confianza: Math.round(mejorPorcentaje),
        razon: mejorCoincidencia.similitud.razon
      };
    }
    
    if (mejorCoincidencia && mejorPorcentaje >= 60) {
      return { 
        tipo: 'parcial', 
        cliente_encontrado: mejorCoincidencia.cliente, 
        confianza: Math.round(mejorPorcentaje),
        razon: mejorCoincidencia.similitud.razon
      };
    }
    
    // Búsqueda específica para juzgados usando palabras clave
    if (nombreBuscado.includes('JUZGADO') || nombreBuscado.includes('TRIBUNAL')) {
      const palabrasExpediente = nombreBuscado.split(' ').filter(p => p.length > 3);
      
      clienteEncontrado = clientes.find(cliente => {
        const nombreCliente = normalizarNombre(cliente.nombre);
        const palabrasCliente = nombreCliente.split(' ').filter(p => p.length > 3);
        
        const coincidencias = palabrasExpediente.filter(palabra => 
          palabrasCliente.some(pc => pc.includes(palabra) || palabra.includes(pc))
        );
        
        return coincidencias.length > 0;
      });
      
      if (clienteEncontrado) {
        return { 
          tipo: 'parcial', 
          cliente_encontrado: clienteEncontrado, 
          confianza: 60,
          razon: 'Coincidencia por palabras clave en entidad judicial'
        };
      }
    }
    
    return { tipo: 'sin_relacion', cliente_encontrado: null, confianza: 0, razon: 'No se encontró coincidencia' };
  };

  // ============================================
  // FUNCIONES EXISTENTES (MANTENIDAS)
  // ============================================

  // Función para verificar integridad de datos
  const verificarIntegridadDatos = async () => {
    try {
      setVerificacionLoading(true);
      setVerificacionResults([]);
      
      const resultados = [];
      
      const expedientes = await expedientesApi.getExpedientes(0, 10000);
      const clientesData = await clientesApi.getClientes(0, 10000);
      const tecnicos = await tecnicosApi.getTecnicos(0, 10000);
      
      resultados.push({
        categoria: 'Estadísticas Generales',
        items: [
          { check: 'Total Expedientes', valor: expedientes.length, estado: 'info' },
          { check: 'Total Clientes', valor: clientesData.length, estado: 'info' },
          { check: 'Total Técnicos', valor: tecnicos.length, estado: 'info' }
        ]
      });
      
      const expedientesConClienteId = expedientes.filter((exp: any) => exp.cliente_id);
      const expedientesConClienteNombre = expedientes.filter((exp: any) => exp.cliente_nombre);
      const expedientesSinCliente = expedientes.filter((exp: any) => !exp.cliente_id && !exp.cliente_nombre);
      
      resultados.push({
        categoria: 'Relaciones Cliente-Expediente',
        items: [
          { 
            check: 'Expedientes con cliente_id asignado', 
            valor: expedientesConClienteId.length, 
            total: expedientes.length,
            estado: expedientesConClienteId.length === expedientes.length ? 'success' : 'warning',
            detalle: `${expedientesConClienteId.length}/${expedientes.length}`
          },
          { 
            check: 'Expedientes con cliente_nombre', 
            valor: expedientesConClienteNombre.length, 
            total: expedientes.length,
            estado: expedientesConClienteNombre.length > 0 ? 'success' : 'warning',
            detalle: `${expedientesConClienteNombre.length}/${expedientes.length}`
          },
          { 
            check: 'Expedientes sin información de cliente', 
            valor: expedientesSinCliente.length, 
            estado: expedientesSinCliente.length === 0 ? 'success' : 'error',
            detalle: expedientesSinCliente.length === 0 ? 'Todo correcto' : `${expedientesSinCliente.length} expedientes sin cliente`
          }
        ]
      });
      
      setVerificacionResults(resultados);
      setShowVerificacion(true);
      setSuccess('Verificación de integridad completada');
      
    } catch (err: any) {
      setError(`Error en la verificación: ${err.message}`);
    } finally {
      setVerificacionLoading(false);
    }
  };

  // Función mejorada para analizar relaciones (usando los algoritmos inteligentes)
  const analizarRelacionesClientes = async () => {
    try {
      setAsignacionLoading(true);
      
      const expedientes = await expedientesApi.getExpedientes(0, 10000);
      const clientesData = await clientesApi.getClientes(0, 10000);
      setClientes(clientesData);
      
      // Filtrar candidatos (usando es_activo en lugar de activo)
      const candidatos = expedientes.filter((exp: any) => 
        exp.cliente_nombre && exp.cliente_nombre.trim() !== "" &&
        (!exp.cliente_id || exp.cliente_id.trim() === "")
      );
      
      if (candidatos.length === 0) {
        setSuccess('No se encontraron expedientes con cliente_nombre sin asignar');
        setAsignacionLoading(false);
        return;
      }
      
      // Obtener nombres únicos
      const nombresUnicosSet = new Set(candidatos.map((exp: any) => exp.cliente_nombre));
      const nombresUnicos = Array.from(nombresUnicosSet);
      
      const claras: any[] = [];
      const dudosas: any[] = [];
      const sinRelacion: any[] = [];
      
      // Analizar cada nombre único usando los algoritmos mejorados
      for (const nombreExp of nombresUnicos) {
        const expedientesConEsteNombre = candidatos.filter((e: any) => e.cliente_nombre === nombreExp);
        const resultado = analizarCoincidenciaCliente(nombreExp, clientesData);
        
        const item = {
          expediente_nombre: nombreExp,
          expedientes_count: expedientesConEsteNombre.length,
          expedientes_ejemplos: expedientesConEsteNombre.slice(0, 3).map((e: any) => e.codigo_actual || e.id),
          expedientes_ids: expedientesConEsteNombre.map((e: any) => e.id),
          ...resultado
        };
        
        if (resultado.tipo === 'exacta') {
          claras.push(item);
        } else if (resultado.tipo === 'parcial') {
          dudosas.push(item);
        } else {
          sinRelacion.push(item);
        }
      }
      
      setRelacionesClaras(claras);
      setRelacionesDudosas(dudosas);
      setExpedientesSinRelacion(sinRelacion);
      setShowAnalisisPrevio(true);
      
      setSuccess(`Análisis completado: ${claras.length} claras, ${dudosas.length} dudosas, ${sinRelacion.length} sin relación`);
      
    } catch (err: any) {
      setError(`Error en el análisis: ${err.message}`);
    } finally {
      setAsignacionLoading(false);
    }
  };

  const ejecutarAsignacionesClaras = async () => {
    try {
      setAsignacionLoading(true);
      setProgress(0);
      
      const results: AsignacionResult[] = [];
      let totalExpedientes = 0;
      
      // Contar total de expedientes a actualizar
      for (const relacion of relacionesClaras) {
        totalExpedientes += relacion.expedientes_count;
      }
      
      let procesados = 0;
      
      for (const relacion of relacionesClaras) {
        for (const expedienteId of relacion.expedientes_ids) {
          try {
            await expedientesApi.updateExpediente(expedienteId, {
              cliente_id: relacion.cliente_encontrado.id
            });
            
            results.push({
              expediente_id: expedienteId,
              expediente_codigo: expedienteId, // Se podría mejorar obteniendo el código
              cliente_original: relacion.expediente_nombre,
              cliente_encontrado: relacion.cliente_encontrado.nombre,
              cliente_id: relacion.cliente_encontrado.id,
              actualizado: true
            });
            
          } catch (err: any) {
            results.push({
              expediente_id: expedienteId,
              expediente_codigo: expedienteId,
              cliente_original: relacion.expediente_nombre,
              cliente_encontrado: null,
              cliente_id: null,
              actualizado: false,
              error: err.message || 'Error desconocido'
            });
          }
          
          procesados++;
          setProgress((procesados / totalExpedientes) * 100);
        }
      }
      
      setAsignacionResults(results);
      setShowResults(true);
      setShowAnalisisPrevio(false);
      
      const exitosos = results.filter(r => r.actualizado).length;
      setSuccess(`Asignaciones claras completadas: ${exitosos}/${totalExpedientes} expedientes actualizados`);
      
    } catch (err: any) {
      setError(`Error en las asignaciones: ${err.message}`);
    } finally {
      setAsignacionLoading(false);
      setProgress(0);
    }
  };

  const supervisarAsignacionesDudosas = () => {
    const expedientesDudosos = relacionesDudosas.flatMap(rel => 
      rel.expedientes_ids.map((id: string) => ({
        expediente_id: id,
        expediente_codigo: id,
        cliente_original: rel.expediente_nombre,
        cliente_encontrado: null,
        cliente_id: null,
        actualizado: false,
        sugerencia: rel.cliente_encontrado,
        confianza: rel.confianza,
        razon: rel.razon
      }))
    );
    
    setExpedientesSinAsignar(expedientesDudosos);
    setShowAsignacionManual(true);
    setShowAnalisisPrevio(false);
    
    // Pre-seleccionar clientes con alta confianza
    const preSelecciones: {[key: string]: string} = {};
    expedientesDudosos.forEach(exp => {
      if (exp.sugerencia && exp.confianza >= 70) {
        preSelecciones[exp.expediente_id] = exp.sugerencia.id;
      }
    });
    setAsignacionesTemporales(preSelecciones);
  };

  const handleAsignacionManual = (expedienteId: string, clienteId: string) => {
    setAsignacionesTemporales(prev => ({
      ...prev,
      [expedienteId]: clienteId
    }));
  };

  const aplicarAsignacionIndividual = async (expedienteId: string) => {
    const clienteId = asignacionesTemporales[expedienteId];
    if (!clienteId) return;

    try {
      await expedientesApi.updateExpediente(expedienteId, {
        cliente_id: clienteId
      });

      setExpedientesSinAsignar(prev => prev.filter(exp => exp.expediente_id !== expedienteId));
      setSuccess(`Expediente asignado correctamente`);
      
    } catch (err: any) {
      setError(`Error asignando expediente: ${err.message}`);
    }
  };

  const aplicarTodasAsignacionesManuales = async () => {
    const asignacionesPendientes = Object.entries(asignacionesTemporales);
    
    if (asignacionesPendientes.length === 0) {
      setError('No hay asignaciones seleccionadas para aplicar');
      return;
    }

    try {
      setLoading(true);
      
      for (const [expedienteId, clienteId] of asignacionesPendientes) {
        await expedientesApi.updateExpediente(expedienteId, {
          cliente_id: clienteId
        });
      }

      setExpedientesSinAsignar(prev => 
        prev.filter(exp => !asignacionesPendientes.some(([id]) => id === exp.expediente_id))
      );
      
      setAsignacionesTemporales({});
      setSuccess(`${asignacionesPendientes.length} expedientes asignados manualmente`);
      
    } catch (err: any) {
      setError(`Error en la asignación masiva: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const configActions: ConfigAction[] = [
    {
      id: 'asignar-clientes',
      title: 'Asignar Clientes a Expedientes',
      description: 'Análisis inteligente con algoritmos avanzados de similitud, normalización y coincidencia por palabras clave.',
      icon: <Link />,
      color: '#1976d2',
      risk: 'medium',
      action: analizarRelacionesClientes,
      disabled: asignacionLoading
    },
    {
      id: 'verificar-integridad',
      title: 'Verificar Integridad de Datos',
      description: 'Revisa la consistencia de datos entre expedientes, clientes y técnicos.',
      icon: <CheckCircle />,
      color: '#388e3c',
      risk: 'low',
      action: verificarIntegridadDatos,
      disabled: verificacionLoading
    },
    {
      id: 'regenerar-codigos',
      title: 'Regenerar Códigos de Expedientes',
      description: 'Regenera códigos faltantes o corrige códigos duplicados.',
      icon: <AutoFixHigh />,
      color: '#f57c00',
      risk: 'high',
      action: () => {
        setError('Función de regeneración de códigos próximamente.');
      }
    },
    {
      id: 'limpiar-relaciones',
      title: 'Limpiar Relaciones Huérfanas',
      description: 'Elimina referencias a registros que ya no existen.',
      icon: <LinkOff />,
      color: '#d32f2f',
      risk: 'high',
      action: () => {
        setError('Función de limpieza próximamente.');
      }
    }
  ];

  const getRiskColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const getRiskText = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low': return 'Bajo Riesgo';
      case 'medium': return 'Riesgo Medio';
      case 'high': return 'Alto Riesgo';
      default: return '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            ⚙️ Configuración General
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Herramientas administrativas con algoritmos inteligentes de asignación
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/')}
          startIcon={<Refresh />}
        >
          Volver al Dashboard
        </Button>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Progreso de asignación */}
      {asignacionLoading && !showAnalisisPrevio && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            🧠 Ejecutando análisis inteligente...
          </Typography>
          <LinearProgress variant={progress > 0 ? "determinate" : "indeterminate"} value={progress} />
           {progress > 0 && <Typography variant="body2" color="textSecondary" mt={1}>
            Progreso: {Math.round(progress)}%
          </Typography>}
        </Paper>
      )}

      {/* Acciones de Configuración */}
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        🔧 Acciones Disponibles
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
        {configActions.map((action) => (
          <Card key={action.id} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Box
                  sx={{
                    backgroundColor: `${action.color}20`,
                    borderRadius: '50%',
                    p: 1,
                    mr: 2,
                    color: action.color
                  }}
                >
                  {action.icon}
                </Box>
                <Box flexGrow={1}>
                  <Typography variant="h6">{action.title}</Typography>
                  <Chip 
                    label={getRiskText(action.risk)} 
                    color={getRiskColor(action.risk)}
                    size="small" 
                  />
                </Box>
              </Box>
              
              <Typography variant="body2" color="textSecondary" mb={3}>
                {action.description}
              </Typography>
              
              <Button
                variant="contained"
                startIcon={action.disabled ? <CircularProgress size={16} /> : <PlayArrow />}
                onClick={action.action}
                disabled={action.disabled || loading || verificacionLoading || asignacionLoading}
                sx={{ 
                    backgroundColor: action.color,
                    '&:hover': {
                        backgroundColor: action.color,
                        filter: 'brightness(0.9)'
                    }
                }}
                fullWidth
              >
                {action.disabled ? 'Ejecutando...' : 'Ejecutar'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Información del Sistema */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📊 Información del Sistema
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="body2" color="textSecondary">Algoritmos Inteligentes</Typography>
            <Typography variant="body1">✅ Levenshtein + Normalización</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Versión</Typography>
            <Typography variant="body1">2.1.0 - IA Mejorada</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Estado</Typography>
            <Chip label="Operativo" color="success" size="small" />
          </Box>
        </Box>
      </Paper>

      {/* Características Mejoradas */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom color="primary">
          🚀 Nuevas Características Inteligentes
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="body2" fontWeight="bold">🧠 Algoritmo Levenshtein</Typography>
            <Typography variant="caption" color="textSecondary">
              Cálculo preciso de similitud entre textos
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="bold">📝 Normalización Avanzada</Typography>
            <Typography variant="caption" color="textSecondary">
              Expansión de abreviaciones y limpieza de texto
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="bold">🔍 Palabras Clave Inteligentes</Typography>
            <Typography variant="caption" color="textSecondary">
              Detección de términos judiciales importantes
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" fontWeight="bold">⚡ Análisis por Grupos</Typography>
            <Typography variant="caption" color="textSecondary">
              Procesamiento eficiente de expedientes agrupados
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Advertencias */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>⚠️ Importante:</strong> Esta versión incluye algoritmos avanzados de IA para mejorar la precisión 
          en la asignación de clientes. Los resultados son más precisos pero requieren más procesamiento.
        </Typography>
      </Alert>

      {/* Modal de Análisis Previo - Versión Mejorada */}
      <Dialog 
        open={showAnalisisPrevio} 
        onClose={() => setShowAnalisisPrevio(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          🧠 Análisis Inteligente de Clientes
        </DialogTitle>
        <DialogContent>
          {/* Resumen con más detalles */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              📊 Resumen del Análisis Inteligente:
            </Typography>
            <Box display="flex" gap={2} mb={2}>
              <Chip 
                label={`${relacionesClaras.length} Relaciones Claras`}
                color="success" 
                variant="filled"
              />
              <Chip 
                label={`${relacionesDudosas.length} Relaciones Dudosas`}
                color="warning"
                variant="filled"
              />
              <Chip 
                label={`${expedientesSinRelacion.length} Sin Relación`}
                color="error" 
                variant="filled"
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              Análisis realizado con algoritmos de similitud Levenshtein, normalización de texto y detección de palabras clave.
            </Typography>
          </Box>

          {/* Relaciones Claras - Versión Mejorada */}
          {relacionesClaras.length > 0 && (
            <Paper sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'success.main' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="success.main">
                  ✅ Relaciones Claras ({relacionesClaras.length} grupos)
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={ejecutarAsignacionesClaras}
                  disabled={asignacionLoading}
                >
                  {asignacionLoading ? 'Ejecutando...' : 'Ejecutar Asignaciones'}
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Coincidencias exactas detectadas por algoritmos inteligentes.
              </Typography>
              
              {/* Mostrar detalles de relaciones claras */}
              <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre Original</TableCell>
                      <TableCell>Cliente Encontrado</TableCell>
                      <TableCell>Confianza</TableCell>
                      <TableCell>Expedientes</TableCell>
                      <TableCell>Razón</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relacionesClaras.map((relacion, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {relacion.expediente_nombre}
                          </Typography>
                        </TableCell>
                        <TableCell>{relacion.cliente_encontrado.nombre}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${relacion.confianza}%`} 
                            color="success" 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{relacion.expedientes_count}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {relacion.razon}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Relaciones Dudosas - Versión Mejorada */}
          {relacionesDudosas.length > 0 && (
            <Paper sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'warning.main' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="warning.main">
                  🔍 Relaciones Dudosas ({relacionesDudosas.length} grupos)
                </Typography>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AutoFixHigh />}
                  onClick={supervisarAsignacionesDudosas}
                >
                  Supervisar Asignaciones
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Posibles coincidencias que requieren revisión manual.
              </Typography>
              
              {/* Mostrar detalles de relaciones dudosas */}
              <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre Original</TableCell>
                      <TableCell>Sugerencia</TableCell>
                      <TableCell>Confianza</TableCell>
                      <TableCell>Expedientes</TableCell>
                      <TableCell>Razón</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {relacionesDudosas.map((relacion, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {relacion.expediente_nombre}
                          </Typography>
                        </TableCell>
                        <TableCell>{relacion.cliente_encontrado?.nombre || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${relacion.confianza}%`} 
                            color={relacion.confianza > 70 ? 'success' : 'warning'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{relacion.expedientes_count}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {relacion.razon}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Sin Relación - Versión Mejorada */}
          {expedientesSinRelacion.length > 0 && (
            <Paper sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'error.main' }}>
              <Typography variant="h6" color="error.main" mb={2}>
                ❌ Sin Relación ({expedientesSinRelacion.length} grupos)
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Nombres que no coinciden con ningún cliente existente.
              </Typography>
              
              <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre Original</TableCell>
                      <TableCell>Expedientes</TableCell>
                      <TableCell>Ejemplos</TableCell>
                      <TableCell>Acción Sugerida</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expedientesSinRelacion.map((relacion, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {relacion.expediente_nombre}
                          </Typography>
                        </TableCell>
                        <TableCell>{relacion.expedientes_count}</TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {relacion.expedientes_ejemplos.join(', ')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label="Crear Cliente" color="info" size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {asignacionLoading && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="body2" color="textSecondary" mt={1} textAlign="center">
                Procesando con algoritmos inteligentes... {Math.round(progress)}%
              </Typography>
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnalisisPrevio(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Asignación Manual - Mejorado */}
      <Dialog 
        open={showAsignacionManual} 
        onClose={() => setShowAsignacionManual(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          🔧 Asignación Manual Supervisada
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Typography variant="h6" gutterBottom>
              📋 Expedientes para Asignación Manual:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Revisa las sugerencias del algoritmo inteligente y confirma las asignaciones.
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <Chip 
                label={`${expedientesSinAsignar.length} Expedientes`}
                color="info" 
              />
              <Chip 
                label={`${Object.keys(asignacionesTemporales).length} Seleccionados`}
                color="success" 
              />
            </Box>
          </Box>

          <Box mb={2}>
            <Button
              variant="contained"
              startIcon={<AutoFixHigh />}
              onClick={aplicarTodasAsignacionesManuales}
              disabled={Object.keys(asignacionesTemporales).length === 0 || loading}
            >
              {loading ? 'Asignando...' : `Asignar Seleccionados (${Object.keys(asignacionesTemporales).length})`}
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Expediente</TableCell>
                  <TableCell>Cliente Original</TableCell>
                  <TableCell>Sugerencia IA</TableCell>
                  <TableCell sx={{ minWidth: 250 }}>Seleccionar Cliente</TableCell>
                  <TableCell>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expedientesSinAsignar.map((exp: any, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {exp.expediente_codigo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {exp.cliente_original || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {exp.sugerencia ? (
                        <Box>
                          <Typography variant="caption" fontWeight="bold">
                            {exp.sugerencia.nombre}
                          </Typography>
                          <Box display="flex" gap={1} mt={0.5}>
                            <Chip 
                              label={`${exp.confianza}%`} 
                              color={exp.confianza > 70 ? 'success' : 'warning'} 
                              size="small"
                            />
                            <Chip 
                              label="IA" 
                              color="info" 
                              size="small"
                            />
                          </Box>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {exp.razon}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Sin sugerencia
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <InputLabel>Asignar Cliente</InputLabel>
                        <Select
                          value={asignacionesTemporales[exp.expediente_id] || ''}
                          label="Asignar Cliente"
                          onChange={(e) => handleAsignacionManual(exp.expediente_id, e.target.value)}
                        >
                          <MenuItem value="">
                            <em>Ninguno</em>
                          </MenuItem>
                          {clientes.map((cliente) => (
                            <MenuItem key={cliente.id} value={cliente.id}>
                              {cliente.nombre}
                              {cliente.codigo && ` (${cliente.codigo})`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Link />}
                        disabled={!asignacionesTemporales[exp.expediente_id]}
                        onClick={() => aplicarAsignacionIndividual(exp.expediente_id)}
                      >
                        Asignar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAsignacionManual(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Resultados - Mejorado */}
      <Dialog 
        open={showResults} 
        onClose={() => setShowResults(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          📊 Resultados de Asignación Inteligente
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body1">
              Procesamiento completado con algoritmos inteligentes:
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <Chip 
                label={`${asignacionResults.filter(r => r.actualizado).length} Exitosos`}
                color="success" 
                size="small" 
              />
              <Chip 
                label={`${asignacionResults.filter(r => !r.actualizado).length} Fallidos`}
                color="error" 
                size="small" 
              />
              <Chip 
                label="Algoritmo IA" 
                color="info" 
                size="small" 
              />
            </Box>
          </Box>
          
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Expediente</TableCell>
                  <TableCell>Cliente Original</TableCell>
                  <TableCell>Cliente Asignado</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Observaciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {asignacionResults.map((result, index) => (
                  <TableRow key={`${result.expediente_id}-${index}`}>
                    <TableCell>{result.expediente_codigo}</TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                        {result.cliente_original || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{result.cliente_encontrado || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={result.actualizado ? 'Asignado' : 'Error'}
                        color={result.actualizado ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {result.error ? (
                        <Typography variant="body2" color="error">
                          {result.error}
                        </Typography>
                      ) : (
                        <Chip label="IA" color="info" size="small" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResults(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Verificación de Integridad */}
      <Dialog 
        open={showVerificacion} 
        onClose={() => setShowVerificacion(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          📊 Verificación de Integridad de Datos
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body1" gutterBottom>
              Resultado del análisis de consistencia de datos:
            </Typography>
          </Box>
          
          {verificacionResults.map((categoria, categoryIndex) => (
            <Paper key={categoryIndex} sx={{ mb: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                {categoria.categoria}
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={1}>
                {categoria.items.map((item: any, itemIndex: number) => (
                  <Box key={itemIndex} display="flex" alignItems="center" justifyContent="space-between" p={1} sx={{ borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2" flexGrow={1}>
                      {item.check}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography variant="body2" color="textSecondary" fontWeight="bold">
                        {item.detalle || item.valor}
                      </Typography>
                      <Chip 
                        label={
                          item.estado === 'success' ? 'OK' :
                          item.estado === 'warning' ? 'Atención' :
                          item.estado === 'error' ? 'Error' : 'Info'
                        }
                        color={
                          item.estado === 'success' ? 'success' :
                          item.estado === 'warning' ? 'warning' :
                          item.estado === 'error' ? 'error' : 'info'
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          ))}
          
          {verificacionLoading && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVerificacion(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfiguracionGeneral;